import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';

const formatTimestamp = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  const date = dt.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date} at ${time}`;
};

/**
 * Tagline appendix shown on each email tile.
 * Surfaces a quick at-a-glance "Last sent: dd Mon yyyy at HH:MM — N of M clubs"
 * line so the user doesn't have to dig through an audit log to know whether
 * yesterday's emails actually went out.
 *
 * Props:
 *   competitionId  — the competition to query
 *   kind           — one of 'entry-confirmation' | 'draft-programme' | 'final-programme'
 *   refreshKey     — bump this number to force a refetch (e.g. after a send)
 */
export default function LastSentLine({ competitionId, kind, refreshKey = 0 }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!competitionId) { setInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/email-history/last-send/${competitionId}/${kind}`,
          { withCredentials: true }
        );
        if (!cancelled) setInfo(res.data);
      } catch {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [competitionId, kind, refreshKey]);

  if (!info || !info.last_sent_at) return null;

  const liveLabel = info.live ? '' : ' (dev mode)';
  const totalLabel = info.total_clubs ? ` of ${info.total_clubs} clubs` : '';
  return (
    <p className="text-xs text-stone-500 mt-1" data-testid={`last-sent-${kind}`}>
      Last sent: {formatTimestamp(info.last_sent_at)} — {info.sent_count}{totalLabel}{liveLabel}
    </p>
  );
}
